import React, { ChangeEvent, memo, useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Checkbox, FormControlLabel } from '@material-ui/core'
import { MenuCombobox } from 'components/base/Combobox'
import { ItemWithLocalization } from 'components/base/Combobox/types'
import { ConfirmationDialog } from 'components/base/ConfirmationDialog/ConfirmationDialog'
import MenuItem from 'components/base/MenuItem'
import { FormContext } from 'contexts/FormContext'
import { ViewSettings } from 'config/constants/editorSettings'
import { TranslationContext } from 'contexts/TranslationContext'
import { selectViewSettings } from 'selectors/mainView'

const LANGUAGES = [
  { value: '', label: 'Browser default' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'bz', label: 'Português (Brasil)' },
  { value: 'it', label: 'Italiano' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
  { value: 'ko', label: '한국어' },
  { value: 'ru', label: 'Русский' },
  { value: 'zh', label: '中文' },
]

const langOptions = LANGUAGES.map(({ value, label }) => ({
  value,
  label,
  dataLangId: '',
  title: '',
})) as Array<ItemWithLocalization>

export const ViewSettingsPanel = memo(() => {
  const { handleInput } = useContext(FormContext)
  const viewSettings = useSelector(selectViewSettings)
  const { t } = useContext(TranslationContext)
  const [ lang, setLang ] = useState(viewSettings[ViewSettings.lang] || '')
  const [ showLangConfirm, setShowLangConfirm ] = useState(false)
  const [ pendingLang, setPendingLang ] = useState('')

  const handleChangeLang = (selectedLang: string) => {
    if (selectedLang === lang) return
    setPendingLang(selectedLang)
    setShowLangConfirm(true)
  }

  const confirmLangChange = () => {
    setLang(pendingLang)
    handleInput({
      target: { name: ViewSettings.lang, value: pendingLang },
    } as unknown as ChangeEvent<HTMLSelectElement>)
    setShowLangConfirm(false)
    window.location.reload()
  }

  return (
    <Box width="100%">
      <form className="view-settings">
        <div className="view-settings__lang-line">
          <span>
            {t('VIEW_SETTINGS_LANG')}
            :
          </span>
          <MenuCombobox
            items={langOptions}
            value={lang}
            anchorWidth
            classes={{ select: 'view-settings__lang-dropdown' }}
          >
            {langOptions.map(item => (
              <MenuItem
                key={item.value}
                selected={lang === item.value}
                onClick={() => handleChangeLang(item.value)}
              >
                {item.label}
              </MenuItem>
            ))}
          </MenuCombobox>
        </div>
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showHeader}
              checked={Boolean(viewSettings[ViewSettings.showHeader])}
            />
          )}
          label={t('SHOW_EDITOR_HEADER')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showMenuTabs}
              checked={Boolean(viewSettings[ViewSettings.showMenuTabs])}
            />
          )}
          label={t('SHOW_MENU_TABS')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showProjectMedia}
              checked={Boolean(viewSettings[ViewSettings.showProjectMedia])}
            />
          )}
          label={t('SHOW_PROJECT_MEDIA_POOL')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showMyMedia}
              checked={Boolean(viewSettings[ViewSettings.showMyMedia])}
            />
          )}
          label={t('SHOW_MY_MEDIA_POOL')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showPreviewSplitButton}
              checked={Boolean(viewSettings[ViewSettings.showPreviewSplitButton])}
            />
          )}
          label={t('SHOW_PREVIEW_SPLIT_BUTTON')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showPreviewSwitchButtons}
              checked={Boolean(viewSettings[ViewSettings.showPreviewSwitchButtons])}
            />
          )}
          label={t('SHOW_PREVIEW_SWITCH_BUTTONS')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showInOutPointsTimeline}
              checked={Boolean(viewSettings[ViewSettings.showInOutPointsTimeline])}
            />
          )}
          label={t('SHOW_INOUT_POINTS_TIMELINE')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showInOutPointsSource}
              checked={Boolean(viewSettings[ViewSettings.showInOutPointsSource])}
            />
          )}
          label={t('SHOW_INOUT_POINTS_SOURCE')}
        />
        <FormControlLabel
          control={(
            <Checkbox
              onChange={handleInput}
              color="primary"
              name={ViewSettings.showDropPlaceHolderTimeline}
              checked={Boolean(viewSettings[ViewSettings.showDropPlaceHolderTimeline])}
            />
          )}
          label={t('SHOW_DROP_PLACE_HOLDER_TIMELINE')}
        />
      </form>
      {showLangConfirm && (
        <ConfirmationDialog
          title={t('MODAL_WINDOW_LANGUAGE_CHANGE_TITLE')}
          confirmBtnText={t('MODAL_WINDOW_BUTTON_RESTART')}
          closeBtnText={t('EDITOR_MAIN_SETTINGS_BTN_CANCEL')}
          onCancel={() => setShowLangConfirm(false)}
          onConfirm={confirmLangChange}
        />
      )}
    </Box>
  )
})
